import React, { useContext, useMemo, useRef, useState } from 'react';
import Add from '@material-ui/icons/Add';
import Clear from '@material-ui/icons/Clear';
import { CREATE, useNotify } from 'react-admin';
import { CreationContactContext } from './CreationContactContext';
import { Form, Field, FormSpy } from 'react-final-form';
import { Button, Grid, IconButton } from '@material-ui/core';
import { userType, userTypeForIA, userTypeSA } from '../UserLookups';
import {
  fieldLengthValidator,
  isStringContainingLessThanOrGreaterThanOrEqualsSign,
  isValidEmailLocalPartAndDomainName,
  isValidEmailRegex
} from '../../../core/utils';
import UserApi from '../../../core/apis/UserApi';
import CheckBoxField from '../../../core/components/CheckBoxField';
import { useStyles } from './StyleStep';
import dataProvider from '../../../core/providers/IccUserDataProvider';
import useNotifyServerError from '../../../core/hooks/useNotifyServerError';
import { isEmpty } from 'lodash';
import { USERTYPE } from '../EnumUserType';
import AccessRolesStep from './AccessRolesStep';
import SelectPermissionGroupsStep from './SelectPermissionGroupsStep';
import AssignPermissionGroupsStep from './AssignPermissionGroupsStep';
import { STEP } from '../UserCreate';
import { createForm } from 'final-form';
import { Select, TextField } from 'mui-rff';

const MAX_SINGLE_ADDRESS_LENGTH = 100;

const checkIfEmailIsValidAndExists = async (value, profile, fieldState) => {
  if (
    !fieldState.active &&
    (!isValidEmailRegex(value) || !isValidEmailLocalPartAndDomainName(value))
  ) {
    return 'Please enter a valid email';
  }

  if (isValidEmailRegex(value) && isValidEmailLocalPartAndDomainName(value)) {
    if (fieldState.active && profile.userType !== USERTYPE.investorAdmin) {
      let error;
      const emailExists = await UserApi.checkIfEmailExists(
        encodeURIComponent(value)
      );

      if (emailExists) {
        for (let i = 0; i < emailExists.totalCount; i++) {
          if (
            emailExists.pageData[i].email.toUpperCase() === value.toUpperCase()
          ) {
            error = 'Email address already exists';
            break;
          } else {
            error = undefined;
          }
        }
      }
      return error;
    }
  }
};

const validateEmail = field => {
  if (!isValidEmailRegex(field)) {
    return 'Please enter a valid email address';
  } else if (field && field.length > MAX_SINGLE_ADDRESS_LENGTH) {
    return (
      'Email address should not contain more than ' +
      MAX_SINGLE_ADDRESS_LENGTH +
      ' characters.'
    );
  }
};

const UserDetailsStep = props => {
  const {
    onSubmitEnd,
    connectedUserProfile,
    variant,
    setContactCreated,
    setActiveListOfSteps,
    contactCreated,
    hasReadUserRolesPermission,
    hasWriteUserRolesPermission,
    hasWriteUserAuthorizationPermission,
    hasReadUserAuthorizationNaPermission,
    activeListOfSteps,
    shouldQuitRef,
    handleClose,
    setIsSpinnerOnNextButton,
    setIsSpinnerOnSaveButton,
    userSet,
    setLeavingPromptListOfSteps,
    leavingPromptListOfSteps
  } = props;
  const { contactContext, setContactContext } = useContext(
    CreationContactContext
  );
  const notifyServerError = useNotifyServerError();
  const classes = useStyles();
  const [isDisable, setIsDisable] = useState(false);
  const notify = useNotify();
  const formStateRef = useRef({});

  // Define the choices in the select input that the connected user will see according to his own userType.
  const userTypeChoices = useMemo(() => {
    if (isEmpty(contactCreated)) {
      if (connectedUserProfile.userType === USERTYPE.superAdmin) {
        return userTypeSA;
      } else if (connectedUserProfile.userType === USERTYPE.fundAdmin) {
        return userType;
      } else {
        return userTypeForIA;
      }
    } else {
      if (contactCreated.userType === USERTYPE.superAdmin) {
        setIsDisable(true);
        return userTypeSA;
      } else if (contactCreated.userType === USERTYPE.fundAdmin) {
        setIsDisable(true);
        return userType;
      } else if (contactCreated.userType === USERTYPE.investorAdmin) {
        setIsDisable(true);
        return userTypeForIA;
      } else {
        return userTypeForIA;
      }
    }
  }, [connectedUserProfile, contactCreated]);

  const initialFormValues = {
    userType: isEmpty(contactCreated)
      ? USERTYPE.regularContact
      : contactCreated.userType,
    salutationName: isEmpty(contactCreated)
      ? ''
      : contactCreated.salutationName,
    firstName: isEmpty(contactCreated) ? '' : contactCreated.firstName,
    lastName: isEmpty(contactCreated) ? '' : contactCreated.lastName,
    company: isEmpty(contactCreated) ? '' : contactCreated.company,
    email: isEmpty(contactCreated) ? '' : contactCreated.email,
    receiveDailySummary: isEmpty(contactCreated)
      ? false
      : contactCreated.receiveDailySummary
  };

  const onSubmit = values => {
    const pristine = formStateRef.current.pristine;

    // If there are ccEmailAddresses in the form values, we store them in one array.
    let ccEmailAddresses = [] || null;
    for (const [key, value] of Object.entries(values)) {
      if (/^ccField/.test(key)) {
        ccEmailAddresses.push(value);
      }
    }

    if (!isEmpty(contactCreated)) {
      ccEmailAddresses.push(...contactCreated.ccEmailAddresses);
    }

    // We build the payload based on the form values and some default values expected on a contact creation
    // isEmpty(ccEmailAddresses) ? null : ccEmailAddresses
    const payload = {
      status: 'INVITED',
      emailOptOut: false,
      syncToExternalSource: false,
      userType: values.userType,
      salutationName: values.salutationName,
      firstName: values.firstName,
      lastName: values.lastName,
      company: values.company,
      email: values.email,
      ccEmailAddresses: ccEmailAddresses,
      receiveDailySummary: values.receiveDailySummary
    };

    // If it's the first submission, we make a POST request.
    if (!pristine && isEmpty(contactCreated)) {
      dataProvider(CREATE, 'users', { data: payload })
        .then(res => {
          if (!isEmpty(res)) {
            setContactCreated(res.data);
            userSet.current = res.data;
            const updatedListOfStep = leavingPromptListOfSteps.map(step => {
              if (step.name === STEP.contactDetails) {
                return { ...step, completed: true };
              }
              return step;
            });
            setLeavingPromptListOfSteps(updatedListOfStep);
          }
        })
        .then(() => {
          if (shouldQuitRef.current === true) {
            handleClose();
            notify('ra.notification.created_contact_successful');
          } else {
            onSubmitEnd();
          }
        })
        .finally(() => {
          shouldQuitRef.current = false;
          setIsSpinnerOnNextButton(false);
          setIsSpinnerOnSaveButton(false);
        })
        .catch(e => notifyServerError(e, undefined));
    }
    // If the form values have been changed and the contact has already been created then it's an update.
    if (!pristine && !isEmpty(contactCreated)) {
      const contactId = contactCreated.id;
      UserApi.updateUser(contactId, payload)
        .then(res => {
          if (!isEmpty(res)) {
            setContactCreated({ ...res, id: contactCreated.userId });
            userSet.current = { ...res, id: contactCreated.userId };
          }
        })
        .then(() => {
          if (shouldQuitRef.current === true) {
            handleClose();
          } else {
            onSubmitEnd();
          }
        })
        .finally(() => {
          setIsSpinnerOnNextButton(false);
          setIsSpinnerOnSaveButton(false);
        })
        .catch(e => notifyServerError(e, undefined));
    }

    // If the contact has already been created and nothing has been changed in the form, we move to next step.
    if (pristine && !isEmpty(contactCreated)) {
      onSubmitEnd();
    }
  };

  const formRef = useRef(
    createForm({
      onSubmit: onSubmit,
      initialValues: initialFormValues
    })
  );

  const handleChangeSelectInput = userTypeSelected => {
    if (
      userTypeSelected === USERTYPE.investorAdmin ||
      userTypeSelected === USERTYPE.fundAdmin ||
      userTypeSelected === USERTYPE.regularContact
    ) {
      if (
        hasWriteUserAuthorizationPermission &&
        hasReadUserAuthorizationNaPermission
      ) {
        if (
          !activeListOfSteps.some(
            step => step.name === STEP.selectPermissionGroups
          ) &&
          !activeListOfSteps.some(
            step => step.name === STEP.assignPermissionGroups
          )
        ) {
          setActiveListOfSteps(currentList =>
            [
              ...currentList,
              {
                name: STEP.selectPermissionGroups,
                label: 'Permission Groups - Select',
                description: null,
                component: SelectPermissionGroupsStep,
                stepNo: 2
              },
              {
                name: STEP.assignPermissionGroups,
                label: 'Permission Groups - Assign',
                description: [
                  'Select permission groups in the matrix below.',
                  'Below are assigned permissions of the selected contact. Select which ones you want to import.'
                ],
                component: AssignPermissionGroupsStep,
                stepNo: 3
              }
            ].sort((a, b) => (a.stepNo > b.stepNo ? 1 : -1))
          );
        }
      }
      if (
        hasReadUserRolesPermission &&
        hasWriteUserRolesPermission &&
        (userTypeSelected === USERTYPE.investorAdmin ||
          userTypeSelected === USERTYPE.fundAdmin)
      ) {
        if (!activeListOfSteps.some(step => step.name === STEP.accessRoles)) {
          setActiveListOfSteps(currentList =>
            [
              ...currentList,
              {
                name: STEP.accessRoles,
                label: 'Access Roles',
                description:
                  'Select Access Roles from the list below to assign them to the Contact.',
                component: AccessRolesStep,
                stepNo: 4
              }
            ].sort((a, b) => (a.stepNo > b.stepNo ? 1 : -1))
          );
        }
      } else if (userTypeSelected === USERTYPE.regularContact) {
        setActiveListOfSteps(currentList =>
          currentList.filter(step => step.name !== STEP.accessRoles)
        );
      }
    } else {
      if (
        activeListOfSteps.some(
          step => step.name === STEP.selectPermissionGroups
        ) &&
        activeListOfSteps.some(
          step => step.name === STEP.assignPermissionGroups
        )
      ) {
        if (activeListOfSteps.some(step => step.name === STEP.accessRoles)) {
          setActiveListOfSteps(currentList =>
            currentList.filter(
              step =>
                step.name !== STEP.selectPermissionGroups &&
                step.name !== STEP.assignPermissionGroups &&
                step.name !== STEP.accessRoles
            )
          );
        } else {
          setActiveListOfSteps(currentList =>
            currentList.filter(
              step =>
                step.name !== STEP.selectPermissionGroups &&
                step.name !== STEP.assignPermissionGroups
            )
          );
        }
      } else {
        setActiveListOfSteps(currentList =>
          currentList.filter(step => step.name !== STEP.accessRoles)
        );
      }
    }
  };

  function required(value) {
    return value ? undefined : 'Required';
  }

  const composeValidators = (...validators) => (value, values, fieldState) =>
    validators.reduce(
      (error, validator) => error || validator(value, values, fieldState),
      undefined
    );

  const previousActiveField = useRef();

  const handleAddCcField = () =>
    formRef.current.change(`ccField-${Date.now()}`, '');
  const handleDeleteCcField = ccEmailKey =>
    formRef.current.change(ccEmailKey, undefined);

  return (
    <Form
      form={formRef.current}
      // We want a validation onBlur on the email field.
      // To achieve that, we must modify the form state with a mutator function
      mutators={{
        triggerValidation(fieldsToValidate, state, { changeValue }) {
          fieldsToValidate.forEach(fieldName => {
            changeValue(state, fieldName, n => n);
          });
        }
      }}
    >
      {({ handleSubmit, submitting, values }) => (
        <form onSubmit={handleSubmit} id="contactDetailsForm" noValidate>
          {
            // By default, all the fields are not validated "onBlur", but we want one of them to be validated that way.
            // We have a reference to store the previous state of the field we want to validate on Blur ("email")
            // If the previous active field was email but the new one is not, we trigger the validation on the email field.
            // The FormSpy component will listen to any "active" event being made on the form.
          }
          <FormSpy subscription={{ active: true }}>
            {({ form }) => {
              if (
                previousActiveField.current === 'email' &&
                !form.getFieldState('email')?.active
              ) {
                form.mutators.triggerValidation(`email`);
              }

              previousActiveField.current = form.getFieldState('email')?.active
                ? 'email'
                : null;
              return null;
            }}
          </FormSpy>
          <FormSpy
            subscription={{ values: true }}
            onChange={({ values: formValues }) => {
              handleChangeSelectInput(formValues.userType);
            }}
          />
          <FormSpy
            subscription={{ pristine: true, valid: true, submitting: true }}
            onChange={({ pristine, valid, submitting: isSubmitting }) => {
              setContactContext({
                ...contactContext,
                detailsFormState: { pristine, valid, isSubmitting }
              });
              formStateRef.current = { pristine };
            }}
          />

          <Grid container>
            <Grid item xs={12} className={classes.formGroup}>
              <Select
                name="userType"
                disabled={isDisable || submitting}
                fieldProps={{ validate: required }}
                data={userTypeChoices}
                label="User Type"
                required
              />
            </Grid>
            <Grid item xs={12} className={classes.formGroup}>
              <TextField
                name="salutationName"
                label="Salutation"
                fieldProps={{ validate: fieldLengthValidator(100) }}
                disabled={submitting}
              />
            </Grid>
            <Grid item xs={12} className={classes.formGroup}>
              <TextField
                name="firstName"
                fieldProps={{
                  validate: composeValidators(
                    required,
                    fieldLengthValidator(64),
                    isStringContainingLessThanOrGreaterThanOrEqualsSign
                  )
                }}
                label="First Name"
                disabled={submitting}
                required
              />
            </Grid>
            <Grid item xs={12} className={classes.formGroup}>
              <TextField
                name="lastName"
                fieldProps={{
                  validate: composeValidators(
                    required,
                    fieldLengthValidator(64),
                    isStringContainingLessThanOrGreaterThanOrEqualsSign
                  )
                }}
                label="Last Name"
                disabled={submitting}
                required
              />
            </Grid>
            <Grid item xs={12} className={classes.formGroup}>
              <TextField
                name="company"
                label="Company Name"
                fieldProps={{ validate: fieldLengthValidator(254) }}
                disabled={submitting}
              />
            </Grid>
            <Grid item xs={12} className={classes.formGroup}>
              <TextField
                name="email"
                type={'email'}
                label="Email"
                fieldProps={{
                  validate: composeValidators(
                    required,
                    fieldLengthValidator(150),
                    (value, _allValue, fieldState) =>
                      checkIfEmailIsValidAndExists(
                        value,
                        connectedUserProfile,
                        fieldState
                      )
                  )
                }}
                disabled={submitting}
                required
              />
            </Grid>
            {Object.keys(values)
              .filter(key => /^ccField/.test(key))
              .map((ccEmailKey, index) => (
                <Grid
                  item
                  xs={12}
                  className={classes.formGroup}
                  key={ccEmailKey}
                >
                  <TextField
                    style={{ width: 'calc(100% - 30px)' }}
                    name={ccEmailKey}
                    label={`CC Email ${(index + 1).toString()}`}
                    fieldProps={{
                      validate: composeValidators(required, validateEmail)
                    }}
                    disabled={submitting}
                  />
                  <IconButton
                    aria-label="delete"
                    style={{
                      position: 'relative',
                      top: '18px',
                      left: '5px'
                    }}
                    onClick={() => handleDeleteCcField(ccEmailKey)}
                  >
                    <Clear />
                  </IconButton>
                </Grid>
              ))}

            <Button
              color={'primary'}
              startIcon={<Add fontSize="small" />}
              variant="text"
              onClick={handleAddCcField}
            >
              Add CC Email
            </Button>
            <Grid item xs={12} className={classes.formCheckBox}>
              <Field
                name="receiveDailySummary"
                component={CheckBoxField}
                label="Receive Daily Summary"
                variant={variant}
                type="checkbox"
              />
            </Grid>
          </Grid>
        </form>
      )}
    </Form>
  );
};

export default UserDetailsStep;
